import { ref, computed } from 'vue'

import useApiStore from '@browser/stores/api'
import { AccessTypeEnum } from '@openapi'
import type { Upload } from '@browser/stores/upload.types'
import type { File } from '@openapi'
import type { AllFileCreateRequest, DeviceFile } from '@lib/types/files'

const MAX_CONCURRENT_TRANSFERS = 2
let onUploadComplete: (fileId: File['id']) => void

const { state: apiState } = useApiStore()

const queue = ref<Upload[]>([])
const uploadSelectedFiles = ref<globalThis.File[] | DeviceFile[]>([])

const transferringFiles = computed(() => queue.value.filter((u: Upload) => u.uploadStatus === 'transferring'))
const pendingFiles = computed(() => queue.value.filter((u: Upload) => u.uploadStatus === 'pending'))
const isQueueBusy = computed(() => !!transferringFiles.value.length || !!pendingFiles.value.length)
const onQueueChange = () => {
  while (pendingFiles.value.length && transferringFiles.value.length < MAX_CONCURRENT_TRANSFERS) {
    const upload = queue.value.find((u: Upload) => u.uploadStatus === 'pending')
    upload && uploadFile(upload)
  }
}
const addUploads = (uploads: Upload[], onUploadCompleteCb?: any) => {
  const current = queue.value.slice()
  onUploadCompleteCb && (onUploadComplete = onUploadCompleteCb)

  uploads.forEach((u) => {
    current.unshift(u)
  })

  uploadSelectedFiles.value = []
  queue.value = current
  onQueueChange()
}

const removeUpload = (id: Upload['id']) => {
  if (!id) return
  queue.value = queue.value.filter((u) => u.id !== id)
}

const clearCompletedAndFailed = () => {
  queue.value = queue.value.filter((u: Upload) => u.uploadStatus === 'transferring' || u.uploadStatus === 'pending')
}

const uploadFile = async (upload: Upload) => {
  upload.uploadStatus = 'transferring'
  try {
    const fileCreateRequest: Omit<AllFileCreateRequest, 'instance_id'> = {
      ...('path' in upload ? { path: upload.path } : { content: upload.content }),
      folder_id: upload.folder_id,
      metadata: upload.metadata,
      permissions: upload.permissions,
      tags: upload.tags,
      access_type: upload.access_type ?? AccessTypeEnum.PUBLIC,
      expires_at: upload.expires_at,
    }
    const createdFile = await apiState.fileCreator?.(fileCreateRequest)

    upload.uploadStatus = 'completed'
    onUploadComplete(createdFile?.id as string)
  } catch (e: any) {
    const isCancel = e?.code === 'ERR_CANCELED'
    upload.uploadStatus = 'failed'
    upload.error = isCancel ? upload.cancelErrorMessage : upload.genericErrorMessage
    !isCancel && console.warn('[upload.store]: upload failed', upload, e)
  } finally {
    onQueueChange()
  }
}

export default function () {
  return {
    state: { queue, isQueueBusy, uploadSelectedFiles },
    actions: { addUploads, removeUpload, clearCompletedAndFailed },
  }
}
